// Where a detail screen's "Volver" goes back to.
//
// The observación detail is reachable from three places — the
// observaciones list, a tienda, and a closed acta (/reportes/:id) — and a
// "Volver a observaciones" hardcoded to the list stranded users who came
// from the acta (founder feedback, 2026-09-26). The opener passes its own
// URL + label through react-router's location state; the detail resolves
// it here and falls back to the list when there is no usable origin
// (deep link, reload, external referrer).
//
// Location state — not history.back() — because the acta opens in its own
// tab (DetalleTienda's timeline links `target="_blank"`), where the
// browser history has nowhere to go back to.

export type BackOrigin = { to: string; label: string };

export const OBSERVACIONES_BACK: BackOrigin = {
  to: "/observaciones",
  label: "Volver a observaciones",
};

const STATE_KEY = "backOrigin";

/** State payload for `navigate(to, { state })` / `<Link state>`. */
export function backOriginState(to: string, label: string): { [STATE_KEY]: BackOrigin } {
  return { [STATE_KEY]: { to, label } };
}

// Only same-app relative paths: a stray "//evil.example" or "https://…"
// in state must never become a navigation target.
function isSafeRelativePath(to: string): boolean {
  return to.startsWith("/") && !to.startsWith("//");
}

/** Reads the origin out of `useLocation().state`, or the list fallback. */
export function resolveBackOrigin(
  state: unknown,
  fallback: BackOrigin = OBSERVACIONES_BACK
): BackOrigin {
  if (typeof state !== "object" || state === null) return fallback;
  const raw = (state as Record<string, unknown>)[STATE_KEY];
  if (typeof raw !== "object" || raw === null) return fallback;
  const { to, label } = raw as Record<string, unknown>;
  if (typeof to !== "string" || typeof label !== "string") return fallback;
  if (!isSafeRelativePath(to) || label.trim() === "") return fallback;
  return { to, label };
}
